import { createContext, useContext, useMemo, useReducer, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import uniqid from 'uniqid';

import {
  trackMarkFloatingButtonClicked,
  trackMarkPanelClosed,
  trackMarkPanelOpened,
} from '../analytics/mark-panel-analytics';

import {
  initialMarkPanelState,
  markPanelReducer,
  resolveOpenIntent,
} from './mark-panel-reducer';
import type {
  MarkContext,
  MarkOpenRequest,
  MarkPageContext,
  MarkPanelActions,
  MarkPanelSession,
  MarkPanelState,
  MarkSurfaceId,
  MarkSurfaceRegistration,
} from './mark-panel.types';

const MarkPanelStateContext = createContext<MarkPanelState | null>(null);
const MarkPanelActionsContext = createContext<MarkPanelActions | null>(null);
const MarkPanelReturnFocusContext =
  createContext<RefObject<HTMLElement | null> | null>(null);

export const useMarkPanelState = (): MarkPanelState | null =>
  useContext(MarkPanelStateContext);

export const useMarkPanelActions = (): MarkPanelActions | null =>
  useContext(MarkPanelActionsContext);

export const useMarkPanelReturnFocus =
  (): RefObject<HTMLElement | null> | null =>
    useContext(MarkPanelReturnFocusContext);

interface MarkPanelProviderProps {
  children: ReactNode;
}

export const MarkPanelProvider = ({ children }: MarkPanelProviderProps) => {
  const [state, dispatch] = useReducer(markPanelReducer, initialMarkPanelState);
  const stateRef = useRef(state);

  stateRef.current = state;

  const registrationsRef = useRef(
    new Map<MarkSurfaceId, MarkSurfaceRegistration>()
  );
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const actions = useMemo<MarkPanelActions>(() => {
    const buildSession = (
      context: MarkContext,
      entryPoint: MarkOpenRequest['entryPoint']
    ): MarkPanelSession | null => {
      const registration = registrationsRef.current.get(context.surface);

      if (!registration) return null;

      return {
        key: uniqid(),
        surfaceId: context.surface,
        clientId: registration.clientId,
        context,
        conversationId: null,
        entryPoint,
      };
    };

    const openWithContext = (request: MarkOpenRequest): void => {
      returnFocusRef.current = request.anchorElement ?? null;

      const wasClosed = stateRef.current.status === 'closed';
      const intent = resolveOpenIntent(stateRef.current, request.context);

      if (intent === 'focusExisting') {
        dispatch({ type: 'focusedExisting' });

        return;
      }

      const session = buildSession(request.context, request.entryPoint);

      if (!session) return;

      const registration = registrationsRef.current.get(
        request.context.surface
      );

      if (wasClosed && registration?.isAnalyticsEnabled) {
        trackMarkPanelOpened({
          surface: request.context.surface,
          entryPoint: request.entryPoint,
          contextKind: request.context.kind,
          contextSection: request.context.section,
        });
      }

      dispatch({ type: 'opened', payload: { session } });
    };

    return {
      registerSurface: (registration) => {
        registrationsRef.current.set(registration.id, registration);
        dispatch({
          type: 'surfaceRegistered',
          payload: { id: registration.id, clientId: registration.clientId },
        });

        return () => {
          const { session, status } = stateRef.current;
          const isSessionOnSurface =
            session?.surfaceId === registration.id &&
            session?.clientId === registration.clientId;

          if (
            isSessionOnSurface &&
            status !== 'closed' &&
            registration.isAnalyticsEnabled
          ) {
            trackMarkPanelClosed({
              surface: registration.id,
              closeMethod: 'navigation',
              hasConversation: session.conversationId != null,
            });
          }

          registrationsRef.current.delete(registration.id);
          dispatch({
            type: 'surfaceUnregistered',
            payload: { id: registration.id, clientId: registration.clientId },
          });
        };
      },
      openWithContext,
      openPageContext: () => {
        const activeSurfaceId = stateRef.current.activeSurface?.id;

        if (!activeSurfaceId) return;

        const registration = registrationsRef.current.get(activeSurfaceId);
        const context = registration?.resolvePageContext();

        if (!context) return;

        if (registration?.isAnalyticsEnabled) {
          trackMarkFloatingButtonClicked({ surface: activeSurfaceId });
        }

        openWithContext({ context, entryPoint: 'floatingButton' });
      },
      close: (method) => {
        const { session } = stateRef.current;

        if (session) {
          const registration = registrationsRef.current.get(session.surfaceId);

          if (registration?.isAnalyticsEnabled) {
            trackMarkPanelClosed({
              surface: session.surfaceId,
              closeMethod: method,
              hasConversation: session.conversationId != null,
            });
          }
        }

        dispatch({ type: 'closed' });
      },
      bindConversation: (sessionKey, conversationId) =>
        dispatch({
          type: 'conversationBound',
          payload: { sessionKey, conversationId },
        }),
      buildPageContext: (): MarkPageContext | null => {
        const { session } = stateRef.current;

        if (!session) return null;

        const registration = registrationsRef.current.get(session.surfaceId);

        if (!registration) return null;

        return registration.buildPageContext(session.context);
      },
    };
  }, []);

  return (
    <MarkPanelStateContext.Provider value={state}>
      <MarkPanelActionsContext.Provider value={actions}>
        <MarkPanelReturnFocusContext.Provider value={returnFocusRef}>
          {children}
        </MarkPanelReturnFocusContext.Provider>
      </MarkPanelActionsContext.Provider>
    </MarkPanelStateContext.Provider>
  );
};
